import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserRound, Flame, Trophy, Sparkles, Crown, Check } from "lucide-react";
import { toast } from "sonner";

function initials(name) {
  if (!name) return "?";
  return name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function memberSince(createdAt) {
  if (!createdAt) return "recently";
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return "recently";
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function daysAsMember(createdAt) {
  if (!createdAt) return null;
  const then = new Date(createdAt);
  if (isNaN(then.getTime())) return null;
  const diff = Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function ProfileDialog({ onClose }) {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === user.name) { onClose(); return; }
    setBusy(true);
    try {
      const { data } = await api.patch("/user/name", { name: trimmed });
      setUser(data);
      toast.success("Name updated");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };

  const days = daysAsMember(user?.created_at);
  const planLabel = user?.plan === "lifetime" ? "Lifetime member"
                 : user?.plan === "pro" ? "Pro"
                 : "Free plan";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md bg-card border rounded-2xl p-0 overflow-hidden animate-slide-up"
        data-testid="profile-dialog"
      >
        <div className="bg-secondary text-secondary-foreground px-6 py-6 flex items-center gap-4 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-honey/20 blur-3xl pointer-events-none" />
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              data-testid="profile-avatar-image"
              className="w-16 h-16 rounded-full object-cover border-2 border-honey shrink-0 relative z-10"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div
              data-testid="profile-avatar-initials"
              className="w-16 h-16 rounded-full bg-terracotta text-white flex items-center justify-center font-display text-2xl shrink-0 border-2 border-honey relative z-10"
            >
              {initials(user?.name)}
            </div>
          )}
          <div className="relative z-10 min-w-0">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-secondary-foreground tracking-tight truncate">
                {user?.name}
              </DialogTitle>
              <DialogDescription className="text-secondary-foreground/70 truncate">
                {user?.email}
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Member since */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <UserRound className="w-4 h-4" strokeWidth={1.75} /> Member since
            </div>
            <div className="text-right">
              <div className="font-medium" data-testid="member-since">
                {memberSince(user?.created_at)}
              </div>
              {days !== null && (
                <div className="text-xs text-muted-foreground" data-testid="member-days">
                  {days === 0 ? "Joined today" : `${days} day${days === 1 ? "" : "s"} in`}
                </div>
              )}
            </div>
          </div>

          {/* Plan badge */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Crown className="w-4 h-4" strokeWidth={1.75} /> Plan
            </div>
            <div
              data-testid="profile-plan"
              className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full ${
                user?.plan === "lifetime" ? "bg-honey text-secondary" :
                user?.plan === "pro" ? "bg-forest text-white" :
                "bg-muted text-foreground"
              }`}
            >
              {planLabel}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
            <div className="text-center">
              <Flame className="w-4 h-4 text-terracotta mx-auto mb-1" strokeWidth={1.75} />
              <div className="font-display text-2xl leading-none">{user?.streak || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Streak</div>
            </div>
            <div className="text-center">
              <Trophy className="w-4 h-4 text-terracotta mx-auto mb-1" strokeWidth={1.75} />
              <div className="font-display text-2xl leading-none">{user?.longest_streak || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Longest</div>
            </div>
            <div className="text-center">
              <Sparkles className="w-4 h-4 text-terracotta mx-auto mb-1" strokeWidth={1.75} />
              <div className="font-display text-2xl leading-none">{user?.completed_dates?.length || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Days</div>
            </div>
          </div>

          {/* Edit name */}
          <div className="pt-2 border-t border-border space-y-2">
            <Label htmlFor="profile-name">Display name</Label>
            <div className="flex gap-2">
              <Input
                id="profile-name"
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
              />
              <Button
                onClick={save} disabled={busy || name.trim() === user?.name || !name.trim()}
                data-testid="profile-save-button"
                className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white shrink-0"
              >
                <Check className="w-4 h-4 mr-1.5" strokeWidth={2} /> Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
